// ==================================================================================================
//
// Profile Graph Bar Chart with D3
//
// ==================================================================================================
import {groupBy, filter, flatten, forEach} from 'lodash'
import m from 'moment'
import UAParser from 'ua-parser-js'
import * as d3 from 'd3'
import {debounce, getCheckins} from '../utilities';
import I18n, { t } from '../i18n';
I18n;

const flatpickr = require("flatpickr")

const DATETIME_FORMAT = 'ddd, DD MMM YYYY HH:mm:ss z'

let navigatedToCheckin = false;

const graphWorkoutChart = () => {
  const props = {
    dateFormat: 'D-M-YYYY',
    dateRangeFormat: 'MMM D, YYYY',
    dateRangeAriaFormat: 'MMMM D, YYYY',
    startDate: m().subtract(2, 'weeks').startOf('day'),
    endDate: m().startOf('day'),
    groupedData: [],
    graphHeight: 150,
    graphWidth: 1200,
    height: 0,
    width: 0,
    mode: 'day',
    data: [],
    displayModes: {
      day: {
        startOf: 'day',
        diff: 'days',
        xFormat: 'MM/D'
      },
      week: {
        startOf: 'week',
        diff: 'weeks',
        xFormat: 'MM/D'
      },
      month: {
        startOf: 'month',
        diff: 'months',
        xFormat: 'MMM'
      },
      year: {
        startOf: 'month',
        diff: 'months',
        xFormat: 'MMM/YY'
      },
      longYears: {
        startOf: 'year',
        diff: 'years',
        xFormat: 'YYYY'
      }
    }
  };

  let currentDataset = []

  let els = {
    $weeklyToggle: null,
    x: null,
    y: null,
    svg: null
  };

  // Cache dom element selectors
  // ------------------------------------------------
  const createChildren = () => {
    els.wrapper = '#js-graph-workout-chart'
    els.$dateRange = $('.js-workout-history-date-range')
    els.$dateRangeFilter = $('.js-workout-history-date-range-filter')
    els.$graphToggles = $('.js-graph-toggle')
    els.$workoutTable = $('.js-workout-table-body')
    els.$datepicker = $('.js-datepicker')
    els.$downloadPDFLink = $('.js-download-pdf')
  }

  function getCurrentDataset() {
    const start = m(props.startDate).startOf(props.displayModes[props.mode].startOf).toDate()
    const end = m(props.endDate)
    return filter(props.groupedData, (val, key) => {
      const eventDate = m(key, props.dateFormat)
      return eventDate.isBetween(start, end, null, '[]')
    }).sort((a, b) => a[0].datetime - b[0].datetime)
  }

  function groupDataset() {
    const zeroedData = props.data.map(ev => {
      return {
        clubname: ev.location,
        originalDatetime: ev.checkin_time.toGMTString(),
        sortingDatetime: ev.checkin_time,
        datetime: m(ev.checkin_time.toGMTString(), DATETIME_FORMAT).startOf(props.displayModes[props.mode].startOf).toDate()
      }
    })

    return groupBy(zeroedData, val => {
      return m(val.datetime).format(props.dateFormat)
    })
  }

  // Plot data on the graph
  // ------------------------------------------------
  const plotDataOnGraph = () => {
    // check to ensure data exists before plotting
    if (currentDataset.length > 0) {
      removeError()
      setTimeout(() => {
        const {startOf, diff, xFormat} = props.displayModes[props.mode]
        const barCount = props.endDate.diff(props.startDate, diff)
        const barDates = []

        for (let i = 0; i <= barCount + 1; i++) {
          barDates.push(m(props.startDate).add(i, diff).startOf(startOf).toDate())
        }

        const maxY = d3.max(currentDataset, set => set.length)

        els.x.domain(barDates)
        els.y.domain([0, maxY])

        // append the rectangles for the bar chart
        const bars = els.svg.selectAll(".bar")
          .data(currentDataset)

        bars.enter().append("rect")
          .attr("class", "bar")
          .merge(bars)
          .attr("x", function(d) { return els.x(d[0].datetime); })
          .attr("width", els.x.bandwidth())
          .attr("y", function(d) { return els.y(d.length); })
          .attr("height", function(d) { return props.height - els.y(d.length); })
          .attr('fill', (d, i) => i % 2 === 0 ? '#28a9ec' : '#3118ba')

        // append the bar labels
        const barLabels = els.svg.selectAll("text.bar-label")
          .data(currentDataset)

        barLabels.enter().append("text")
          .attr("class", "bar-label")
          .merge(barLabels)
          .text(function(d) { return d.length })
          .attr("x", function(d) { return els.x(d[0].datetime) + els.x.bandwidth() / 2; })
          .attr("y", function(d) { return els.y(d.length) - 5; })
          .attr("text-anchor", "middle")

        // add the x Axis
        els.svg.select('.x.axis')
          .call(d3.axisBottom(els.x).tickSizeOuter(0).tickFormat(d => m(d).format(xFormat)))
          .selectAll("text")
          .attr('class', 'x-axis-labels')

        if (window.CrunchMemberPortal.currentBreakpoint === 'sm' || props.mode == 'year') {
          els.svg.selectAll('.x-axis-labels')
            .attr("y", 0)
            .attr("x", 9)
            .attr("dy", ".35em")
            .attr("transform", "rotate(90)")
            .attr("text-anchor", "start")
        }

        // add the y Axis
        els.yAxis.tickValues([0, maxY])
        els.svg.select('.y.axis')
          .call(els.yAxis)

        barLabels.exit().remove()
        bars.exit().remove()
      }, 850);

      //show new error message only if no checkins AND error message doesnt already exist
    } else if (currentDataset.length == 0 && $('.member-profile-graph-message--no-data').length == 0) {
        const $noDataExistsMessage = $(`<div class="member-profile-graph-message member-profile-graph-message--no-data"><p class="type--center type--d6 type--charcoal-50" aria-role="alert">${I18n.t('no_checkins')}</p></div>`)
        showError(true, $noDataExistsMessage)
    }
  }

  // Set up graph element
  // ------------------------------------------------
  const setUpGraph = () => {
    setTimeout(() => {
      const {xFormat} = props.displayModes[props.mode]
      const margin = { top: 20, right: 0, bottom: 30, left: 40 }
      props.width = $(els.wrapper).width() // allows for responsive graph
      props.height = $(els.wrapper).outerHeight() // allows for responsive graph

      const widthSpace = props.width + margin.left + margin.right
      const heightSpace = props.height + margin.top + margin.bottom + 10

      // set the ranges
      els.x = d3.scaleBand()
        .rangeRound([0, props.width])
        .padding(0.1)

      els.y = d3.scaleLinear()
        .range([props.height, 0])

      // append the svg object to the body of the page
      els.svg = d3.select(els.wrapper)
        .append("svg")
        .attr("preserveAspectRatio", "xMinYMin meet")
        .attr("viewBox", "0 0 " + widthSpace + " " + heightSpace)
        .classed("svg-content", true)
        .append("g")

      // quick test for ie11 to size svg height properly
      const parsed = new UAParser(navigator.userAgent)
      const isIE11 = (parsed.getBrowser().name === "IE" && parsed.getBrowser().major === "11") ? true : false

      if (isIE11) {
        let setHeight = parseInt(heightSpace) + margin.left
        $('.svg-content').attr('height', setHeight) // force height attr if ie11
      }

      // add x axis
      els.svg.append('g').attr('class', 'x axis')
        .attr("transform", "translate(0," + props.height + ")")

      // add y axis
      els.yAxis = d3.axisLeft(els.y).tickFormat(d3.format(".0f"))
      els.svg.append('g').attr('class', 'y axis')

    // add horizontal grid lines
      els.svg.append('g')
        .attr("class", "grid horizontal")
        .call(d3.axisLeft(els.y)
          .ticks(2)
          .tickSize(-(props.width), 0, 0)
          .tickFormat("")
        )
    }, 800);  //Add timeout to give time to page to load to get graph dimensions

  }

  // Update date range
  // ------------------------------------------------
  const updateUIDate = () => {
    const {dateRangeFormat, dateRangeAriaFormat, startDate, endDate} = props
    const dateRangeText = startDate.format(dateRangeFormat) + ' - ' + endDate.format(dateRangeFormat)
    const dateRangeFilterText = startDate.format(dateRangeFormat) + ' | ' + endDate.format(dateRangeFormat)
    const dateRangeAria = startDate.format(dateRangeAriaFormat) + ' to ' + endDate.format(dateRangeAriaFormat)

    els.$dateRange.text(dateRangeText)
    els.$dateRangeFilter.text(dateRangeFilterText)
    els.$dateRange.siblings('.js-workout-history-date-range-for-aria').text(dateRangeAria)
  }

  const updateDateRange = (start, end = m().startOf('day')) => {
    props.startDate = start
    props.endDate = end
    updateUIDate()
    props.groupedData = groupDataset()
    currentDataset = getCurrentDataset()
    plotDataOnGraph()
    updateTable()
    updateDownloadRange()
  }

  const updateTable = () => {
    const rowData = flatten(currentDataset).sort((a, b) => b.sortingDatetime - a.sortingDatetime)
    els.$workoutTable.empty()
    els.$workoutTable.append($(rowTemplate(rowData)))
  }

  const updateDownloadRange = () => {
    const start = props.startDate.format('DD-MM-YYYY')
    const end = props.endDate.format('DD-MM-YYYY')
    const currHref = els.$downloadPDFLink.attr('href')
    const endIndex = currHref.indexOf('?') === -1 ? currHref.length : currHref.indexOf('?')
    const href = `${currHref.substring(0, endIndex)}?start=${start}&end=${end}`
    els.$downloadPDFLink.attr('href', href)
    const fade = currentDataset.length === 0 ? 'fadeOut' : 'fadeIn'
    els.$downloadPDFLink[fade](500)
  }

  const rowTemplate = rowData => {
    return rowData.reduce((acc, cur) => {
      return acc + `
        <div class="member-profile-workout-chart__row" aria-label="Club check-in">
          <p class="member-profile-workout-chart__row__date type--d6 type--charcoal--80">
            <span class="is-visually-hidden">Date:</span>
            <span>${m(cur.originalDatetime, DATETIME_FORMAT).format('MMM DD, YYYY')}</span>
          </p>
          <p class="member-profile-workout-chart__row__time type--d6 type--charcoal--80">
            <span class="is-visually-hidden">Time:</span>
            <span>${m(cur.originalDatetime, DATETIME_FORMAT).format('h:mm a')}</span>
          </p>
          <p class="member-profile-workout-chart__row__location type--d6 type--charcoal--80">
            <span class="is-visually-hidden">Location:</span>
            <span>${cur.clubname}</span>
          </p>
        </div>
      `
    }, '')
  }

  const toggleActiveUIViewItem = (e) => {
    els.$graphToggles.removeClass('is-active')

    if (e.type === 'click') {
      const $newItem = $(e.currentTarget)
      $newItem.addClass('is-active')

      let gaLabel = 'View ' + $newItem.data('ga-label')
      els.$downloadPDFLink.attr('data-ga-label', gaLabel)
    } else {
      els.$datepicker.addClass('is-active')
    }
  }

  // Show graph & remove error message
  // ------------------------------------------------
  const removeError = () => {
    if ($(els.wrapper).hasClass('was-removed')) {
      $('.member-profile-graph-message').fadeOut(500, () => {
        $('.member-profile-graph-message').remove()
        $(els.wrapper).removeClass('was-removed')
        $(els.wrapper).parents('.member-profile-graph-wrapper__interior').fadeIn(500)

        if ($(els.wrapper).hasClass('all-removed')) {
          $('.js-workout-chart-table').slideDown(500)
          $(els.wrapper).removeClass('all-removed')
        }
      })
    }
  }

  // Hide graph & show error message
  // ------------------------------------------------
  const showError = (hideAll, errorMessage, datepickerInstance) => {
    if (hideAll) {
      $('.js-workout-chart-table').slideUp(500)
      $(els.wrapper).addClass('all-removed')
    }

    $(els.wrapper).parents('.member-profile-graph-wrapper__interior').slideUp(500, () => {
      // append 'narrow search' message & clear the datepicker
      $(els.wrapper).parents('.member-profile-graph-wrapper').append(errorMessage).hide().delay(30).slideDown(500)
      if (datepickerInstance) datepickerInstance.clear()
    })

    // add class to denote the graph was removed
    // and don't update the date range
    $(els.wrapper).addClass('was-removed')
  }

  // On date picker change, reset date mode
  // and pass through new data
  // ------------------------------------------------
  const onDatePickerChange = (selectedDates, instance) => {
    window.dispatchEvent(new Event('checkinSelect'))
    // only run new data if datepicker has 2 dates selected, and they are unique
    if (selectedDates.length === 2) {
      const theStart = m(selectedDates[0])
      const theEnd = m(selectedDates[1])
      const distance = m(theStart).diff(theEnd, 'days') * -1 // always -1 since dates are always past

      if (distance < 15) {
        props.mode = 'day'
      } else if (distance < 84) {
        props.mode = 'week'
      } else if (distance < 366) {
        props.mode = 'month'
      } else if (distance < 731) {
        props.mode = 'year'
      } else {
        props.mode = 'longYears'
      }

      updateDateRange(theStart, theEnd)
      instance.clear() // clear the datepicker

      els.$downloadPDFLink.attr('data-ga-label', 'View pdf')
    } else {

      const defaultEndDate = m(instance.config.maxDate).startOf('day');
      const defaultStartDate = m(defaultEndDate).subtract(2, 'weeks').startOf('day');
      props.mode = 'day'
      updateDateRange(defaultStartDate, defaultEndDate);
    }
  }

  // Init the datepicker
  // ------------------------------------------------
  const initDatepicker = () => {
    let datepickerEl = document.querySelector('.js-datepicker-wrapper');
    let maxDateAttribute = datepickerEl.getAttribute('data-max-date');
    props.endDate = maxDateAttribute ? m(+maxDateAttribute) : m();
    props.startDate = m(props.endDate).subtract(2, 'weeks');
    els.datepickerElem = els.$datepicker.flatpickr({
      mode: 'range',
      appendTo: datepickerEl,
      maxDate: props.endDate.format("YYYY-MM-DD"), // do not allow picking of future dates
      disableMobile: 'true',
      ariaDateFormat: 'l, F j, Y',
      onReady: (selectedDates, dateStr, instance) => {
        $('.flatpickr-calendar').attr('aria-hidden', true)
        forEach(document.querySelectorAll('.flatpickr-month'), month => { month.setAttribute('aria-hidden', 'true') })
        forEach(document.querySelectorAll('.flatpickr-weekdays'), weekday => { weekday.setAttribute('aria-hidden', 'true') })
        $('.flatpickr-weekdaycontainer').css('display', 'flex')
        $('.flatpickr-weekdaycontainer').css('width', '100%')
        $('.flatpickr-prev-month').css('top', '10%')
        $('.flatpickr-next-month').css('top', '10%')
        $('.flatpickr-calendar').addClass('rightAligned')
      },
      onOpen: (selectedDates, dateStr, instance) => toggleActiveUIViewItem(instance.element),
      onChange: (selectedDates, dateStr, instance) => {
        // add more specific aria label info to end date
        if (selectedDates.length === 2) {
          let selectedEndDateElem = document.querySelector('.flatpickr-day.selected.endRange')
          if (selectedEndDateElem) {
            let selectedEndDateAriaLabel = selectedEndDateElem.getAttribute('aria-label')
            selectedEndDateElem.setAttribute('aria-label', 'End date selected ' + selectedEndDateAriaLabel)
          }
        }

        // add more specific aria label info to initial date
        if (selectedDates.length >= 1) {
          let selectedDateElem = document.querySelector('.flatpickr-day.selected:not(.endRange)')
          if (selectedDateElem) {
            let selectedDateAriaLabel = selectedDateElem.getAttribute('aria-label')
            selectedDateElem.setAttribute('aria-label', 'Start date selected ' + selectedDateAriaLabel)
          }
        }
      },
      onClose: (selectedDates, dateStr, instance) => {
        onDatePickerChange(selectedDates, instance)

        $('.flatpickr-calendar').attr('aria-hidden', true)
      }
    });
  }

  // Ensure smooth responsiveness on resize
  // ------------------------------------------------
  const resetGraph = () => {
    d3.select(els.wrapper).selectAll('svg').remove()

    setTimeout(() => {
      setUpGraph()
      plotDataOnGraph()
    }, 10)
  }

  // Open datepicker & set to aria visible
  // ------------------------------------------------
  const openDatepicker = () => {
    els.datepickerElem.open()
    $('.flatpickr-calendar').attr('aria-hidden', false)
    $('.flatpickr-day.today').attr('aria-describedby', 'datepicker-hint')
    $('.flatpickr-day.today').attr('tabindex', '1')
    $('.flatpickr-day.today').focus()
  }

  // Get checkin data from server
  // ------------------------------------------------
  const getCheckinData = () => {
    const memberId = els.$workoutTable.data('member-id')
    getCheckins(memberId, onCheckinSuccess, onCheckinError)
  }

  const onCheckinSuccess = (data) => {
    props.data = data.map(el => {
      return Object.assign(el, {
        checkin_time: new Date(el.checkin_time)
      })
    })
    props.groupedData = groupDataset()
    currentDataset = getCurrentDataset()
    plotDataOnGraph()
    updateTable()
    updateDownloadRange()
  }

  const onCheckinError = (data) => {
  }

  const flagCheckinTab = () => {
    if (!navigatedToCheckin) {
      navigatedToCheckin = true;
      resetGraph()
    }
  }

  // Enable
  // ------------------------------------------------
  const enable = () => {
    if (props.isEnabled) return;

    // if svg already exists due to turbolinks, remove it
    if ($(els.wrapper).find('svg').length) $(els.wrapper).empty()

    // Add your event handlers here
    initDatepicker()
    updateUIDate()

    els.$datepicker.on('click', openDatepicker)

    props.onWindowResize = debounce(resetGraph, 200);
    window.addEventListener('resize', props.onWindowResize);
    window.addEventListener('checkinSelect', props.onWindowResize);
    window.addEventListener('checkinSelectFromOutside', flagCheckinTab);

    props.isEnabled = true;
  }

  // Disable
  // ------------------------------------------------
  const disable = () => {
    if (!props.isEnabled) return;

    // Remove your event handlers here
    els.$datepicker.off('click', openDatepicker)

    props.onWindowResize = debounce(resetGraph, 50);
    window.removeEventListener('resize', props.onWindowResize);

    props.isEnabled = false;
  }

  // Init
  // ------------------------------------------------
  const init = () => {
    createChildren();
    enable()
    getCheckinData();
  }

  return {
    init,
    enable,
    disable
  };
}

export default graphWorkoutChart;
